/**
 * CheckoutCancelPage
 * Page shown when user cancels checkout
 */

import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  AppBar,
  Toolbar,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Info as InfoIcon,
  ShoppingCart as CartIcon,
  Phone as PhoneIcon,
  Help as HelpIcon,
} from '@mui/icons-material';
import { Link, useSearchParams } from 'react-router-dom';
import { trackCheckoutCancel, trackPageView } from '../lib/analytics';
import { useCartStore } from '../store/cart';

const CheckoutCancelPage: React.FC = () => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const itemCount = useCartStore(state => state.getItemCount());

  useEffect(() => {
    trackPageView('checkout_cancel');
    trackCheckoutCancel(sessionId || undefined);
  }, [sessionId]);

  const helpOptions = [
    {
      icon: <CartIcon />,
      title: 'Return to Cart',
      description: 'Review your items and try checkout again',
      action: {
        label: 'View Cart',
        to: '/cart',
      },
    },
    {
      icon: <PhoneIcon />,
      title: 'Talk to Engineering',
      description: 'Have questions? Our team is here to help',
      action: {
        label: 'Contact Us',
        to: '/contact',
      },
    },
    {
      icon: <HelpIcon />,
      title: 'Need Different Options?',
      description: 'Explore our pricing and customize your package',
      action: {
        label: 'View Pricing',
        to: '/pricing',
      },
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar>
          <Typography
            variant="h6"
            component={Link}
            to="/"
            sx={{
              flexGrow: 1,
              textDecoration: 'none',
              color: 'primary.main',
              fontWeight: 'bold',
            }}
          >
            REDEX
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 8 }}>
        {/* Info Header */}
        <Box textAlign="center" sx={{ mb: 6 }}>
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.info.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <InfoIcon sx={{ fontSize: 60, color: 'info.main' }} />
          </Box>
          
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Checkout Cancelled
          </Typography>
          <Typography variant="body1" color="text.secondary">
            No worries! Your cart is still saved and ready when you are.
          </Typography>
          {itemCount > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              You have {itemCount} item{itemCount > 1 ? 's' : ''} in your cart
            </Typography>
          )}
        </Box>

        {/* Help Options */}
        <Paper sx={{ p: 4, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            What would you like to do?
          </Typography>
          
          <List disablePadding>
            {helpOptions.map((option, index) => (
              <ListItem
                key={index}
                sx={{
                  px: 0,
                  py: 2,
                  borderBottom: index < helpOptions.length - 1 
                    ? `1px solid ${theme.palette.divider}` 
                    : 'none',
                }}
              >
                <ListItemIcon sx={{ color: 'primary.main' }}>
                  {option.icon}
                </ListItemIcon>
                <ListItemText
                  primary={option.title}
                  secondary={option.description}
                />
                <Button
                  variant="outlined"
                  size="small"
                  component={Link}
                  to={option.action.to}
                >
                  {option.action.label}
                </Button>
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* Common Questions */}
        <Paper sx={{ p: 4 }}>
          <Typography variant="h6" gutterBottom>
            Common Questions
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                Why did my payment fail?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Payment issues can occur due to insufficient funds, card restrictions, 
                or incorrect card details. Try a different payment method or contact your bank.
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                Can I save my cart for later?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Yes! Your cart is automatically saved in your browser. Just come back 
                to this site and your items will still be there.
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                Need a custom quote?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Contact our engineering team for custom packages, volume discounts, 
                or enterprise pricing options.
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default CheckoutCancelPage;
